<template>
  <div class="camera-component">
    <!-- Solo mostramos el selector de modo si no hay foto confirmada -->
    <div v-if="!confirmed" class="mode-selector">
      <div class="mode-toggle">
        <button 
          @click="mode = 'camera'" 
          :class="{ active: mode === 'camera' }"
          class="mode-btn"
        >
          <svg class="mode-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14.828 14.828a4 4 0 0 1-5.656 0"></path>
            <path d="M9 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"></path>
            <rect x="3" y="4" width="18" height="16" rx="2"></rect>
            <path d="M7 2h10"></path>
          </svg>
          Cámara
        </button>
        <button 
          @click="mode = 'upload'" 
          :class="{ active: mode === 'upload' }"
          class="mode-btn"
        >
          <svg class="mode-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7,10 12,15 17,10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Subir
        </button>
      </div>
    </div>

    <!-- Vista de cámara -->
    <div v-if="mode === 'camera' && !confirmed && !photoTaken && !useNativeCamera" class="camera-view">
      <div class="camera-container">
        <video ref="video" class="camera-feed" autoplay playsinline :class="{ 'mirror-effect': facingMode === 'user' }" ></video>
        <canvas ref="canvas" class="photo-preview" style="display: none;"></canvas>
        
        <!-- Overlay con controles -->
        <div class="camera-overlay">
          <div class="camera-controls">
            <button @click="flipCamera" class="control-btn flip-btn" title="Voltear cámara">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                <path d="M3 21v-5h5"></path>
              </svg>
            </button>
            
            <button @click="takePhoto" class="capture-btn">
              <div class="capture-inner"></div>
            </button>
            
            <div class="spacer"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botón para tomar foto con cámara nativa -->
    <div v-if="mode === 'camera' && !confirmed && !photoTaken && useNativeCamera" class="upload-view">
      <input
        type="file"
        ref="nativeCameraInput"
        accept="image/*"
        capture="environment"
        @change="handleNativeCapture"
        style="display: none;"
      />
      <div class="upload-area" @click="$refs.nativeCameraInput.click()">
        <div class="upload-content">
          <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21,15 16,10 5,21"></polyline>
          </svg>
          <h3>Tomar Foto</h3>
          <p>Se abrirá la cámara del dispositivo</p>
        </div>
      </div>
    </div>

    <!-- Vista de subida de imagen -->
    <div v-else-if="mode === 'upload' && !confirmed && !photoTaken" class="upload-view">
      <input 
        type="file" 
        ref="fileInput" 
        accept="image/*" 
        @change="handleFileUpload" 
        style="display: none;"
      >
      
      <div v-if="!uploadedImage" class="upload-area" @click="$refs.fileInput.click()">
        <div class="upload-content">
          <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21,15 16,10 5,21"></polyline>
          </svg>
          <h3>Seleccionar Foto</h3>
          <p>Haz clic para elegir una imagen desde tu dispositivo</p>
        </div>
      </div>
      
      <div v-if="uploadedImage" class="image-preview">
        <div class="preview-container">
          <img :src="uploadedImage" alt="Foto subida" class="preview-image">
        </div>
        <div class="photo-actions">
          <button @click="cancelUpload" class="action-btn cancel-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Cancelar
          </button>
          <button @click="confirmUpload" class="action-btn confirm-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="20,6 9,17 4,12"></polyline>
            </svg>
            Confirmar
          </button>
        </div>
      </div>
    </div>

    <!-- Vista de foto capturada -->
    <div v-else-if="photoTaken && !confirmed" class="photo-taken-view">
      <div class="preview-container">
        <img :src="photoDataUrl" alt="Foto capturada" class="captured-photo">
      </div>
      <div class="photo-actions">
        <button @click="retakePhoto" class="action-btn cancel-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
            <path d="M3 21v-5h5"></path>
          </svg>
          Repetir
        </button>
        <button @click="confirmPhoto" class="action-btn confirm-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <polyline points="20,6 9,17 4,12"></polyline>
          </svg>
          Confirmar
        </button>
      </div>
    </div>

    <!-- Vista de foto confirmada -->
    <div v-else-if="confirmed" class="confirmed-view">
      <div class="success-indicator">
        <svg class="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22,4 12,14.01 9,11.01"></polyline>
        </svg>
        <h3>¡Foto confirmada!</h3>
      </div>
      <div class="preview-container">
        <img :src="finalImage" alt="Foto confirmada" class="confirmed-photo">
      </div>
      <button @click="resetComponent" class="action-btn primary-btn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M14.828 14.828a4 4 0 0 1-5.656 0"></path>
          <path d="M9 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"></path>
          <rect x="3" y="4" width="18" height="16" rx="2"></rect>
          <path d="M7 2h10"></path>
        </svg>
        Tomar otra foto
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SCameraSunExpert',
  data() {
    return {
      mode: 'camera',
      photoTaken: false,
      photoDataUrl: '',
      uploadedImage: '',
      facingMode: 'user',
      stream: null,
      confirmed: false,
      finalImage: '',
    };
  },
  methods: {
    handleNativeCapture(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.match('image.*')) {
        alert('Por favor, selecciona una imagen válida.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.photoDataUrl = e.target.result;
        this.photoTaken = true;
      };
      reader.readAsDataURL(file);
    },

    async startCamera() {
      try {
        if (this.stream) {
          this.stream.getTracks().forEach(track => track.stop());
        }

        const constraints = {
          video: { 
            facingMode: this.facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.$refs.video.srcObject = this.stream;
      } catch (error) {
        console.error('Error al acceder a la cámara:', error);
        alert('No se pudo acceder a la cámara. Asegúrate de haber otorgado los permisos necesarios.');
      }
    },
    
    takePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      
      // Aplicar efecto espejo si es cámara frontal
      if (this.facingMode === 'user') {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      this.photoDataUrl = canvas.toDataURL('image/png');
      this.photoTaken = true;
      
      this.stopCamera();
    },
    
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
    },
    
    flipCamera() {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      this.startCamera();
    },
    
    retakePhoto() {
      this.photoTaken = false;
      this.photoDataUrl = '';
      this.startCamera();
    },
    
    confirmPhoto() {
      this.finalImage = this.photoDataUrl;
      this.confirmed = true;
      this.$emit('photo-confirmed', this.photoDataUrl);
      this.stopCamera();
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!file.type.match('image.*')) {
        alert('Por favor, selecciona un archivo de imagen válido.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadedImage = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    confirmUpload() {
      this.finalImage = this.uploadedImage;
      this.confirmed = true;
      this.$emit('photo-confirmed', this.uploadedImage);
    },

    cancelUpload() {
      this.uploadedImage = '';
      this.$refs.fileInput.value = '';
    },

    resetComponent() {
      this.confirmed = false;
      this.finalImage = '';
      this.photoTaken = false;
      this.uploadedImage = '';
      this.mode = 'camera';
      this.startCamera();
    }
  },

  computed: {
    isMobileDevice() {
      return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
    useNativeCamera() {
      console.log(this.isMobileDevice);
      return this.isMobileDevice;
    }
  },
  
  mounted() {
    if (this.mode === 'camera') {
      this.startCamera();
    }
  },
  
  beforeDestroy() {
    this.stopCamera();
  },
  
  watch: {
    mode(newVal) {
      if (newVal === 'camera') {
        this.startCamera();
      } else {
        this.stopCamera();
      }
    }
  }
};
</script>

<style scoped>
.camera-feed.mirror-effect {
  transform: scaleX(-1);
}

.camera-component {
  max-width: 600px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
}

/* Mode Selector */
.mode-selector {
  margin-bottom: 24px;
}

.mode-toggle {
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
}

.mode-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #666666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-btn.active {
  background: #ffffff;
  color: #1976d2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.mode-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.5);
  color: #333333;
}

.mode-icon {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

/* Camera View */
.camera-container {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #000000;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.camera-feed {
  width: 100%;
  height: 400px;
  object-fit: cover;
  display: block;
}

.camera-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  padding: 24px;
}

.camera-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.control-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #333333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.control-btn:hover {
  background: #ffffff;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.control-btn svg {
  width: 20px;
  height: 20px;
  stroke-width: 2;
}

.capture-btn {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #1976d2;
  border: 4px solid #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.3);
}

.capture-btn:hover {
  background: #1565c0;
  transform: scale(1.05);
}

.capture-btn:active {
  transform: scale(0.95);
}

.capture-inner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #ffffff;
}

.spacer {
  width: 44px;
}

/* Upload Area */
.upload-area {
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
}

.upload-area:hover {
  border-color: #1976d2;
  background: #f3f8ff;
}

.upload-content h3 {
  margin: 16px 0 8px;
  color: #333333;
  font-size: 18px;
  font-weight: 500;
}

.upload-content p {
  margin: 0;
  color: #666666;
  font-size: 14px;
}

.upload-icon {
  width: 48px;
  height: 48px;
  color: #999999;
  stroke-width: 1.5;
  margin: 0 auto;
}

/* Preview Container */
.preview-container {
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
}

.preview-image,
.captured-photo,
.confirmed-photo {
  width: 100%;
  height: auto;
  display: block;
  max-height: 400px;
  object-fit: cover;
}

/* Action Buttons */
.photo-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
}

.action-btn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.cancel-btn {
  color: #d32f2f;
  border-color: #ffcdd2;
}

.cancel-btn:hover {
  background: #ffebee;
  border-color: #d32f2f;
}

.confirm-btn {
  color: #2e7d32;
  border-color: #c8e6c9;
}

.confirm-btn:hover {
  background: #e8f5e8;
  border-color: #2e7d32;
}

.primary-btn {
  color: #1976d2;
  border-color: #bbdefb;
}

.primary-btn:hover {
  background: #e3f2fd;
  border-color: #1976d2;
}

/* Success View */
.success-indicator {
  text-align: center;
  margin-bottom: 24px;
}

.success-icon {
  width: 56px;
  height: 56px;
  color: #2e7d32;
  stroke-width: 2;
  margin-bottom: 12px;
}

.success-indicator h3 {
  margin: 0;
  color: #333333;
  font-size: 20px;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 640px) {
  .camera-component {
    margin: 16px;
    padding: 16px;
  }
  
  .camera-feed {
    height: 300px;
  }
  
  .mode-btn {
    font-size: 12px;
    padding: 10px 12px;
  }
  
  .mode-icon {
    width: 16px;
    height: 16px;
  }
  
  .capture-btn {
    width: 64px;
    height: 64px;
  }
  
  .capture-inner {
    width: 40px;
    height: 40px;
  }
  
  .photo-actions {
    flex-direction: column;
  }
  
  .action-btn {
    justify-content: center;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.photo-taken-view,
.confirmed-view,
.image-preview {
  animation: fadeIn 0.3s ease-out;
}
</style>